import {
  ENDPOINTS,
  PipelineStatusRequestSchema,
  type PipelineStatusRequest,
  type PipelineStatusResponse,
} from '@clm/shared-types';

import { apiGet, type Query } from './client';

/**
 * `GET /v1/admin/pipeline/status` — CLM-70.
 *
 * The one read behind the Admin **Pipeline** surface and the stage tracker on
 * the Contract record. Every query parameter is optional: `contract_type`
 * narrows every figure and list, `contract_key` adds the five-step timeline for
 * that one contract, and `tags` (CLM-77, repeated `tags=` — the all-of filter)
 * narrows all five statements. The request is validated `.strict()` before anything is
 * sent and the response is parsed against the registry schema, so an
 * off-contract body is a `ZodError`, never a half-rendered panel.
 *
 * NFR-06: no encoder here ever emits a `scope` key — scope is the API's, from
 * the token.
 */

const ENDPOINT = ENDPOINTS['pipeline-status'];

export function pipelineStatusQuery(request: PipelineStatusRequest): Query {
  return {
    ...(request.contract_type === undefined ? {} : { contract_type: request.contract_type }),
    ...(request.contract_key === undefined ? {} : { contract_key: request.contract_key }),
    /* CLM-77 follow-up — the Pipeline surface's tag filter was set in state but never encoded, so it never reached the API. */
    ...(request.tags === undefined ? {} : { tags: [...request.tags] }),
  };
}

export async function fetchPipelineStatus(
  request: PipelineStatusRequest,
  signal?: AbortSignal,
): Promise<PipelineStatusResponse> {
  const validated = PipelineStatusRequestSchema.parse(request);
  const raw = await apiGet<unknown>(ENDPOINT.path, {
    query: pipelineStatusQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return ENDPOINT.response.parse(raw);
}
